import axios, { CancelTokenSource } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { getModifiers } from 'actions/modifiers'
import useDispatch from 'hooks/useDispatch'
import useStableStringArray from 'hooks/useStableStringArray'
import { buildTranslate } from 'locales'
import { selectModifiers } from 'selectors/modifier'
import { selectPagination } from 'selectors/pagination'
import { ResolvedAxiosAction } from 'utilities/types'

const t = buildTranslate('errors')

type AxiosCancellationError = {
  error?: { data: string }
}

type UseModifiersParams = {
  search_term?: string
  page?: number
  item_ids?: string[]
  option_ids?: string[]
}

type UseModifiersOptions = {
  shouldSkip?: boolean
  preventCancellation?: boolean
  paginationKey?: string
  method?: 'get' | 'post'
}

export default function useModifiers(
  params: UseModifiersParams = {},
  options: UseModifiersOptions = {}
) {
  const {
    shouldSkip = false,
    preventCancellation = false,
    paginationKey,
    method = 'get',
  } = options

  // Automatically determine pagination key based on request type
  const effectivePaginationKey =
    paginationKey ||
    (params.option_ids && params.option_ids.length > 0 ? 'selected' : 'all')

  const dispatch = useDispatch()

  const [initialLoadCompleted, setInitialLoadCompleted] = useState(false)
  const [isApiLoading, setIsApiLoading] = useState(false)
  // `completed` counts finishes (success or failure), `active` counts
  // requests in flight — counters, not booleans, because preventCancellation
  // lets fetches overlap. One state so a finish commits atomically: observers
  // must never see the decrement and the completion land separately.
  const [fetchCounts, setFetchCounts] = useState({ completed: 0, active: 0 })
  const [error, setError] = useState<Error | null>(null)

  // Extract values with stable defaults
  const searchTerm = params.search_term ?? ''
  const page = params.page ?? 1
  const itemIds = params.item_ids
  const optionIds = params.option_ids

  // Memoize item_ids and option_ids safely
  const stableItemIds = useStableStringArray(itemIds)
  const stableOptionIds = useStableStringArray(optionIds)

  // Keep track of the current fetch request to cancel it if needed
  const abortControllerRef = useRef<CancelTokenSource | null>(null)
  // Keep track of whether the current request should be protected from cancellation
  const preventCancellationRef = useRef<boolean>(false)

  // Selectors
  const modifiers = useSelector(state => selectModifiers(state))
  const pagination =
    useSelector(state =>
      selectPagination(state, 'modifier', effectivePaginationKey)
    ) ?? undefined

  const handleSuccess = () => {
    setIsApiLoading(false)
  }

  const handleError = (error: unknown) => {
    setError(
      error instanceof Error ? error : new Error(t('error_page_message'))
    )
    setIsApiLoading(false)
  }

  const fetchModifiers = useCallback(async () => {
    const requestCancelledMessage = 'Request was canceled due to new one.'

    // If there's already an existing request and it's not protected from cancellation, cancel it
    if (abortControllerRef.current && !preventCancellationRef.current) {
      abortControllerRef.current.cancel(requestCancelledMessage)
    }

    // Create a new cancel token for the current request
    const cancelTokenSource = axios.CancelToken.source()
    abortControllerRef.current = cancelTokenSource
    // Store whether this request should be protected from cancellation
    preventCancellationRef.current = preventCancellation

    try {
      setIsApiLoading(true)
      setFetchCounts(counts => ({ ...counts, active: counts.active + 1 }))
      // Error reflects the latest fetch — a stale error from a prior request
      // must not outlive the retry that supersedes it.
      setError(null)
      const response = (await dispatch(
        getModifiers({
          itemIds: stableItemIds as string[],
          optionIds: stableOptionIds as string[],
          searchTerm,
          page,
          cancelToken: cancelTokenSource.token,
          method,
        })
      )) as ResolvedAxiosAction<ReturnType<typeof getModifiers>> &
        AxiosCancellationError

      // Skip to 'finally' block if request is cancelled
      if (response?.error?.data === requestCancelledMessage) {
        return
      }

      // Handle a valid error response
      if (response?.error) {
        handleError(response?.error)
        return
      }

      handleSuccess()
    } catch (error) {
      handleError(error)
    } finally {
      setInitialLoadCompleted(true)
      setFetchCounts(counts => ({
        completed: counts.completed + 1,
        active: counts.active - 1,
      }))
    }
  }, [
    dispatch,
    stableItemIds,
    stableOptionIds,
    searchTerm,
    page,
    preventCancellation,
    method,
  ])

  useEffect(() => {
    if (shouldSkip) {
      setInitialLoadCompleted(true)
      return
    }
    setInitialLoadCompleted(false)
    fetchModifiers()
  }, [fetchModifiers, shouldSkip])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.cancel('Component unmounted')
      }
    }
  }, [])

  return {
    modifiers,
    pagination,
    isReady: initialLoadCompleted && !isApiLoading,
    isLoading: isApiLoading,
    completedFetchCount: fetchCounts.completed,
    activeFetchCount: fetchCounts.active,
    fetchModifiers,
    error,
  }
}
